import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { adminButton } from '@/components/admin/ui';
import { AiUpgradePrompt } from '@/components/admin/AiUpgradePrompt';
import { useAiDraftQuote, type AiFailure } from '@/hooks/useAiFeatures';
import { lowestPlanWith } from '@/lib/plan-limits';
import { formatQuoteMoney, lineTotal } from '@/lib/quotes';
import type { AiQuoteDraft } from '@/lib/ai-quote-draft';
import type { CurrencyCode } from '@/lib/currency';
import { cn } from '@/lib/utils';

const detailsSchema = z.object({
  details: z
    .string()
    .trim()
    .min(10, 'Tell us a bit more about the job (at least a sentence).')
    .max(2000, 'Keep it under 2,000 characters.'),
});
type DetailsValues = z.infer<typeof detailsSchema>;

export type ApplyDraftMode = 'replace' | 'append';

/**
 * "Draft with AI" on the quote editor (Business plan). The owner describes the
 * job; the ai-draft-quote function returns suggested lines (priced from their
 * own services where they match), a scope summary, assumptions, exclusions and
 * a validity period. Nothing is saved or sent: "Use this draft" fills the
 * editor and the owner reviews it there.
 */
export function AiDraftQuoteDialog({
  open,
  onOpenChange,
  orgId,
  allowed,
  customerName,
  frequency,
  currency,
  hasExistingLines,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string | null;
  /** From the plan limits; undefined while loading. */
  allowed: boolean | undefined;
  customerName: string;
  frequency: string;
  currency: CurrencyCode;
  hasExistingLines: boolean;
  onApply: (draft: AiQuoteDraft, mode: ApplyDraftMode) => void;
}) {
  const draftQuote = useAiDraftQuote();
  const [draft, setDraft] = useState<AiQuoteDraft | null>(null);
  const [problem, setProblem] = useState<{ code: string; message: string } | null>(null);
  const form = useForm<DetailsValues>({ resolver: zodResolver(detailsSchema), defaultValues: { details: '' } });

  useEffect(() => {
    if (!open) {
      setProblem(null);
      draftQuote.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onGenerate = form.handleSubmit(async ({ details }) => {
    if (!orgId) return;
    setProblem(null);
    const result = await draftQuote.mutateAsync({ organizationId: orgId, details, customerName, frequency });
    if (result.ok) setDraft(result.draft);
    else setProblem({ code: (result as AiFailure).code, message: (result as AiFailure).message });
  });

  const apply = (mode: ApplyDraftMode) => {
    if (!draft) return;
    onApply(draft, mode);
    setDraft(null);
    form.reset({ details: '' });
    onOpenChange(false);
  };

  const estimateCount = draft?.lines.filter((l) => l.price_source !== 'price_list').length ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            Draft with AI
          </DialogTitle>
          <DialogDescription>
            Describe the job in your own words. We’ll suggest lines from your services, a summary of the work and
            what’s included. You check everything before anything is saved or sent.
          </DialogDescription>
        </DialogHeader>

        {allowed === false ? (
          <AiUpgradePrompt feature="AI proposal writer" plan={lowestPlanWith('aiProposalWriter')}>
            Type a few notes about a job and get a full quote back: lines priced from your own services, a scope of
            work, assumptions and exclusions.
          </AiUpgradePrompt>
        ) : draft ? (
          <div className="space-y-4" data-testid="ai-draft-preview">
            {draft.title && <p className="text-[15px] font-semibold text-foreground">{draft.title}</p>}
            {draft.scope_summary && <p className="text-[13.5px] leading-relaxed text-foreground">{draft.scope_summary}</p>}

            <div className="space-y-1.5">
              {draft.lines.map((line, index) => (
                <div
                  key={`${line.description}-${index}`}
                  className={cn(
                    'flex flex-col gap-1 rounded-lg border p-2.5 text-[13px] sm:flex-row sm:items-center sm:justify-between',
                    line.is_optional ? 'border-dashed border-border' : 'border-border bg-muted/30',
                  )}
                  data-testid={`ai-draft-line-${index}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">
                      {line.description}
                      {line.is_optional && <span className="ml-1.5 text-[11.5px] font-normal text-muted-foreground">(optional extra)</span>}
                    </p>
                    <p className="text-[12px] text-muted-foreground">
                      {line.quantity} × {formatQuoteMoney(line.unit_price, currency)}
                      {line.note ? ` · ${line.note}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <PriceSourceBadge source={line.price_source} />
                    <span className="adm-num font-semibold text-foreground">
                      {formatQuoteMoney(lineTotal({ quantity: line.quantity, unit_amount: line.unit_price }), currency)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {estimateCount > 0 && (
              <p className="flex items-start gap-2 rounded-lg bg-amber-500/10 p-2.5 text-[12.5px] text-amber-900 dark:text-amber-200">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                {estimateCount === 1 ? '1 price is an AI estimate' : `${estimateCount} prices are AI estimates`}, not from
                your price list. Check {estimateCount === 1 ? 'it' : 'them'} before you send.
              </p>
            )}

            <DraftList title="What we’ve assumed" items={draft.assumptions} />
            <DraftList title="Not included" items={draft.exclusions} />
            <DraftList title="Worth checking with the customer (just for you)" items={draft.questions} />
            <p className="text-[12.5px] text-muted-foreground">Valid for {draft.valid_for_days} days.</p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={onGenerate} className="space-y-3">
              <FormField
                control={form.control}
                name="details"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>About the job</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={5}
                        placeholder="e.g. 3-bed house end of tenancy, oven, carpets in 2 bedrooms, needs doing Friday"
                        disabled={draftQuote.isPending || allowed === undefined}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Rooms, extras, access, timing: anything that affects the price.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {problem && (
                <p
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-2.5 text-[13px] text-foreground"
                  data-testid="ai-draft-problem"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  {problem.message}
                </p>
              )}
            </form>
          </Form>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {allowed === false ? (
            <Button type="button" size="sm" variant="ghost" className={adminButton.outline} onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : draft ? (
            <>
              <Button key="restart" type="button" size="sm" variant="ghost" className={adminButton.ghost} onClick={() => setDraft(null)}>
                <RotateCcw />
                Start again
              </Button>
              {hasExistingLines && (
                <Button key="append" type="button" size="sm" variant="ghost" className={adminButton.outline} onClick={() => apply('append')}>
                  Add to my lines
                </Button>
              )}
              <Button key="replace" type="button" size="sm" className={adminButton.primary} onClick={() => apply('replace')}>
                {hasExistingLines ? 'Replace my lines' : 'Use this draft'}
              </Button>
            </>
          ) : (
            <Button
              key="generate"
              type="button"
              size="sm"
              className={adminButton.primary}
              onClick={onGenerate}
              disabled={draftQuote.isPending || !orgId || allowed === undefined}
            >
              {draftQuote.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />}
              {draftQuote.isPending ? 'Drafting…' : 'Draft my quote'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriceSourceBadge({ source }: { source: AiQuoteDraft['lines'][number]['price_source'] }) {
  return source === 'price_list' ? (
    <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">Your price</span>
  ) : (
    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:text-amber-200">
      AI estimate
    </span>
  );
}

function DraftList({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1">
      <p className="adm-label">{title}</p>
      <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-foreground">
        {items.map((item, i) => (
          <li key={`${item}-${i}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}
